import { HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable, of, throwError } from 'rxjs';
import { map, shareReplay, switchMap, take } from 'rxjs/operators';

import {
    ApiQuery,
    CustomHttpParamEncoder,
    FragmentId,
    PaginatedResults,
    PaginationInfo,
    PartialRecord,
    QueryPart,
    QueryPartStrategy,
    RejectionList,
    ShPaginationService,
    ShRecordService,
    SortInfo
} from '@stylehaven/components';
import { CustomErrorType } from '../../entities/common/custom-error-type.entity';
import { Identifiable } from '../../entities/common/identifiable.interface';
import { Facet } from '../../entities/facet/facet.entity';
import { RecordSecurityGroups } from '../../entities/management/record-security-groups.entity';
import { LuceneHelper } from '../../helpers';
import { MapObjectToHttpParams } from '../../helpers/map-obj-to-http-params.helper';
import { waitForIndex } from '../../helpers/wait-for-index.helper';
import { AbstractHttpService } from '../../services/http/http-service.abstract';
import { ApiFragment, ApiFragmentList } from '../entities/api-fragment.entity';
import { CreateRecord } from '../entities/create-record.entity';
import { EventSearch } from '../entities/events/event-search.entity';
import { HistoryEvent } from '../entities/events/history-event.entity';
import { FacetRequest } from '../entities/facet-request.entity';
import { IndexSync } from '../entities/index-sync.entity';
import { DottedKey, MetadataField } from '../entities/metadata-field.entity';
import { Metadata, PartialMetadata } from '../entities/metadata.entity';
import { RecordSearch } from '../entities/record-search.entity';
import { FragmentService } from './fragment.service';

@Injectable()
export class RecordService implements ShRecordService {
    private readonly ENDPOINT: string = 'records';

    constructor(
        private httpService: AbstractHttpService,
        private fragmentService: FragmentService,
        private luceneHelper: LuceneHelper,
        private readonly paginationService: ShPaginationService
    ) {}

    create(createRecord: CreateRecord): Observable<ApiFragment> {
        return this.httpService
            .post<Metadata>(this.ENDPOINT, createRecord)
            .pipe(map(metadata => new ApiFragment(metadata)));
    }

    createSync(createRecord: CreateRecord): Observable<ApiFragment> {
        return this.create(createRecord).pipe(
            switchMap(record => this.waitForRecordToBeIndexed(record.getRecordId()).pipe(map(() => record)))
        );
    }

    search(searchParams: RecordSearch): Observable<PaginatedResults<ApiFragment>> {
        return this.httpService
            .post<ApiFragmentList>(`${this.ENDPOINT}/search`, searchParams)
            .pipe(map(result => this.fragmentService.parsePaginatedApiResponse(result, searchParams.NrOfResults)));
    }

    searchCount(searchParams: Partial<RecordSearch>): Observable<number> {
        return this.httpService
            .head(this.ENDPOINT, MapObjectToHttpParams(searchParams, true))
            .pipe(map(response => Number(response.headers.get('Result-Count'))));
    }

    getEvents(recordId: string, search: EventSearch): Observable<HistoryEvent[]> {
        return this.httpService.get<HistoryEvent[]>(
            `${this.ENDPOINT}/${recordId}/events`,
            MapObjectToHttpParams(search)
        );
    }

    getQRCode(recordId: string, params: any): Observable<Blob> {
        return this.httpService.getBlob(`${this.ENDPOINT}/${recordId}/qr.png`, params);
    }

    // Returns record as digihaven ApiFragment
    getByRecordId(recordId: string): Observable<ApiFragment> {
        return this.httpService
            .get<Metadata>(`${this.ENDPOINT}/${recordId}`)
            .pipe(map(result => new ApiFragment(result)));
    }

    getRecordWithExif(recordId: string): Observable<ApiFragment> {
        const params: HttpParams = new HttpParams({ encoder: new CustomHttpParamEncoder() }).set('fields', 'Exif');
        const request = `${this.ENDPOINT}/${recordId}?${params.toString()}`;
        return this.httpService.get<Metadata>(request).pipe(map(result => new ApiFragment(result)));
    }

    // Returns record as stylehaven PartialRecord
    getRecord(recordId: string): Observable<PartialRecord> {
        return this.getByRecordId(recordId).pipe(map(apiFragment => apiFragment.toPartialRecord()));
    }

    /**
     * @deprecated
     * Only place where getRecords is actually called is in the viewer-sandbox.service.ts
     * @param paginationInfo
     * @param sortInfo
     * @param query
     */
    getRecords(
        paginationInfo: PaginationInfo,
        sortInfo: SortInfo,
        query: ApiQuery
    ): Observable<PaginatedResults<PartialRecord>> {
        throw new Error('Method not implemented.');
    }

    getAllRecords(query: ApiQuery): Observable<PartialRecord[]> {
        throw new Error('Method not implemented.');
    }

    getFacets(facetRequest: FacetRequest, subject: string = 'records'): Observable<Facet[]> {
        return this.httpService.post(`${subject}/facets`, facetRequest, {}, 'v2');
    }

    waitForRecordCountIndex(indexSync: IndexSync<number>): Observable<number> {
        // Execute count twice to account for out of sync solr instances (See DM-916)
        return this.searchCount(indexSync.search).pipe(
            switchMap(first =>
                indexSync.predicate(first)
                    ? this.searchCount(indexSync.search) // run second check
                    : throwError(CustomErrorType.IndexTimeout)
            ),
            switchMap(second =>
                indexSync.predicate(second)
                    ? of(second) // both passed
                    : throwError(CustomErrorType.IndexTimeout)
            ),
            waitForIndex(indexSync.tries, indexSync.delayInMilliseconds)
        );
    }

    waitForRecordToBeIndexed(recordId: string, versionId?: string): Observable<void> {
        return this.waitForRecordExistence(recordId, versionId, 1);
    }

    waitForRecordsExistence(recordIds: string[]): Observable<void> {
        const queryParts: QueryPart[] = [
            new QueryPart({
                field: DottedKey.RecordId,
                values: recordIds,
                strategy: QueryPartStrategy.MatchAtLeastOne
            })
        ];

        const search: RecordSearch = {
            Q: this.luceneHelper.queryToLuceneQuery(new ApiQuery(queryParts))
        };

        return this.waitForRecordCountIndex({
            predicate: count => count === recordIds.length,
            search
        }).pipe(map(() => void 0));
    }

    waitForRecordToBeRemovedFromIndex(recordId: string, versionId?: string): Observable<void> {
        return this.waitForRecordExistence(recordId, versionId, 0);
    }

    waitForRecordsToBeRemovedFromIndex(recordIds: string[]): Observable<void> {
        const queryParts: QueryPart[] = [
            new QueryPart({
                field: DottedKey.RecordId,
                values: recordIds,
                strategy: QueryPartStrategy.MatchAtLeastOne
            })
        ];

        const search: RecordSearch = {
            Q: this.luceneHelper.queryToLuceneQuery(new ApiQuery(queryParts))
        };

        return this.waitForRecordCountIndex({
            predicate: count => count === 0,
            search
        }).pipe(map(() => void 0));
    }

    waitForRecordUpdate(
        recordId: string,
        version: string | undefined,
        currentLastModifiedDate: string
    ): Observable<void> {
        const idAndVersionQueryParts = this.getIdAndVersionQueryParts(recordId, version);

        const lastModifiedDateQueryPart: QueryPart = new QueryPart({
            field: MetadataField.LastModifiedDate,
            strategy: QueryPartStrategy.MatchNone,
            values: [currentLastModifiedDate]
        });

        const search: RecordSearch = {
            Q: this.luceneHelper.queryToLuceneQuery(
                new ApiQuery([...idAndVersionQueryParts, lastModifiedDateQueryPart])
            )
        };

        return this.waitForRecordCountIndex({
            predicate: count => count === 1,
            search
        }).pipe(map(() => void 0));
    }

    private waitForRecordExistence(
        recordId: string,
        version: string | undefined,
        expectedCount: number
    ): Observable<void> {
        const queryParts: QueryPart[] = this.getIdAndVersionQueryParts(recordId, version);

        const search: RecordSearch = {
            Q: this.luceneHelper.queryToLuceneQuery(new ApiQuery(queryParts))
        };

        return this.waitForRecordCountIndex({
            predicate: count => count === expectedCount,
            search
        }).pipe(map(() => void 0));
    }

    private getIdAndVersionQueryParts(recordId: string, version?: string): QueryPart[] {
        if (!version) {
            const queryPart: QueryPart = new QueryPart({
                field: MetadataField.MediaObjectId,
                strategy: QueryPartStrategy.MatchAtLeastOne,
                values: [recordId]
            });
            return [queryPart];
        } else {
            const versionIdQueryPart: QueryPart = new QueryPart({
                field: MetadataField.VersioningId,
                strategy: QueryPartStrategy.MatchAtLeastOne,
                values: [recordId]
            });

            const versionQueryPart: QueryPart = new QueryPart({
                field: MetadataField.VersioningVersion,
                strategy: QueryPartStrategy.MatchAtLeastOne,
                values: [version]
            });

            return [versionIdQueryPart, versionQueryPart];
        }
    }

    getSecurityGroups(record: Identifiable): Observable<RecordSecurityGroups> {
        if (record.Version) {
            return this.httpService.get<RecordSecurityGroups>(
                `${this.ENDPOINT}/${record.Id}/versions/${record.Version}/security-groups/`
            );
        }

        return this.httpService.get<RecordSecurityGroups>(`${this.ENDPOINT}/${record.Id}/security-groups/`);
    }

    updateSync(record: ApiFragment): Observable<ApiFragment> {
        return this.update(record.getRecordId(), record).pipe(
            switchMap(result =>
                this.waitForRecordUpdate(
                    record.getRecordId(),
                    undefined,
                    record.getField(DottedKey.LastModifiedDate)
                ).pipe(map(() => result))
            )
        );
    }

    update(recordId: string, updateBody: PartialMetadata | ApiFragment): Observable<ApiFragment> {
        const update: PartialMetadata = updateBody instanceof ApiFragment ? updateBody.metadata : updateBody;

        return this.httpService
            .put<Metadata>(`${this.ENDPOINT}/${recordId}`, update)
            .pipe(map(result => new ApiFragment(result)));
    }

    save(recordId: string, updateRecord: PartialMetadata | ApiFragment): Observable<ApiFragment> {
        const update: PartialMetadata = updateRecord instanceof ApiFragment ? updateRecord.metadata : updateRecord;

        const updateBody: any = {
            Metadata: update
        };

        // TODO: Reformat HTTPService apiVersion to enum and add 'mediahaven' api
        const api = 'mediahaven-rest-api/v2/records';
        const request = `${api}/${recordId}`;

        const saveStream$: Observable<ApiFragment> = this.httpService.post<ApiFragment>(
            request,
            updateBody,
            {
                responseType: 'blob' as 'json',
                headers: new HttpHeaders({
                    Accept: ['application/json'],
                    'Content-Type': 'application/json'
                })
            },
            'custom'
        );
        return saveStream$;
    }

    public getRejections(recordId: string, includeCandidates = true): Observable<RejectionList[]> {
        const params: HttpParams = new HttpParams().append('includeCandidates', includeCandidates);
        return this.paginationService
            .getAll<RejectionList>(`v2/${this.ENDPOINT}/${recordId}/rejections`, params, new HttpHeaders())
            .pipe(take(1), shareReplay());
    }

    public registerConsultation(fragmentId: FragmentId): Observable<boolean> {
        return of(true);
    }
}
